import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Crown, 
  Loader2,
  Download,
  LayoutDashboard,
  Users,
  Settings,
  Bell,
  Menu,
  MoreHorizontal,
  ArrowUpRight,
  ShieldCheck
} from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'user' | 'admin';
  subscription_status: 'normal' | 'pro';
  daily_ai_usage_count: number;
  max_daily_credits?: number;
  created_at: string;
  avatar_url?: string;
  last_credit_reset?: string;
}

// Production API URL 
const API_URL = 'https://nutty-alicia-chrono-session-server-e76d814f.koyeb.app';

const AdminPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  // Pagination & Filter State
  const [currentPage, setCurrentPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState<'all' | 'pro' | 'normal'>('all');
  const ITEMS_PER_PAGE = 12; // Increased density

  // Sidebar State - Closed by default on mobile
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [search]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
         navigate('/signin?redirect=/admin'); 
         return;
      }

      const token = session.access_token;
      
      const response = await fetch(`${API_URL}/api/admin/users?search=${search}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 403) {
        setError('Access Denied: You are not an admin.');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch');

      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error(err);
      setError('Failed to load users. Backend connection error.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (userId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'normal' ? 'pro' : 'normal';
    setActionLoading(userId);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/subscription`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Update failed');

      const result = await response.json();

      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, ...result.user } : u
      ));
    } catch (err) {
      alert('Failed to update subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const exportCSV = () => {
     const headers = ['ID', 'Name', 'Email', 'Role', 'Status', 'Credits', 'Created At'];
     const csvContent = [
       headers.join(','),
       ...users.map(u => [
         u.id, 
         `"${u.name || ''}"`, 
         u.email, 
         u.role, 
         u.subscription_status, 
         `${u.daily_ai_usage_count}/${u.max_daily_credits || (u.subscription_status === 'pro' ? 15 : 5)}`,
         u.created_at
       ].join(','))
     ].join('\n');

     const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
     const link = document.createElement('a');
     link.href = URL.createObjectURL(blob);
     link.download = `chrono_users_export.csv`;
     link.click();
  };

  // Filter & Pagination Logic
  const filteredUsers = users.filter(user => {
    if (filterStatus === 'all') return true;
    return user.subscription_status === filterStatus;
  });

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE, 
    currentPage * ITEMS_PER_PAGE
  );

  // Show loading FIRST to prevent dashboard flash
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f0f2f5]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          <p className="text-gray-500 text-sm">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f0f2f5] p-4">
        <div className="text-center p-8 bg-white rounded-lg shadow-sm border border-gray-200 max-w-md w-full">
          <div className="text-red-500 mb-4 flex justify-center"><ShieldCheck size={48} /></div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <button onClick={() => navigate('/')} className="px-4 py-2 bg-gray-900 text-white text-sm rounded hover:bg-gray-800">
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F4F6] font-sans flex text-[#111827]">
      
      {/* Mobile Backdrop Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Dark Enterprise Style */}
      <aside className={`bg-[#1e293b] text-white fixed inset-y-0 left-0 z-40 w-60 transition-transform duration-300 transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static lg:block flex flex-col`}>
         <div className="h-16 flex items-center justify-between px-5 border-b border-gray-700/50">
            <div className="flex items-center">
               <img src="/logo.png" alt="Logo" className="w-7 h-7 object-contain mr-3 opacity-90" />
               <span className="font-semibold text-lg tracking-tight text-white">Chrono<span className="text-blue-400">Admin</span></span>
            </div>
            {/* Close button for mobile */}
            <button 
               onClick={() => setIsSidebarOpen(false)} 
               className="lg:hidden p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-md"
            >
               <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
               </svg>
            </button>
         </div>

         <div className="p-3 space-y-1 mt-2">
             {/* Main Navigation */}
            <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2 px-3 mt-4">Module</div>
            <button className="w-full flex items-center gap-3 px-3 py-2 text-[13px] font-medium rounded-md bg-blue-600 text-white shadow-sm">
               <Users className="w-4 h-4" />
               Users
            </button>
            <button className="w-full flex items-center gap-3 px-3 py-2 text-[13px] font-medium rounded-md text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
               <LayoutDashboard className="w-4 h-4" />
               Analytics
            </button>
            <button className="w-full flex items-center gap-3 px-3 py-2 text-[13px] font-medium rounded-md text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
               <Settings className="w-4 h-4" />
               Settings
            </button>
         </div>

         <div className="mt-auto p-4 border-t border-gray-700/50">
            <div className="flex items-center gap-3">
               <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-xs font-bold text-white">
                  AD
               </div>
               <div className="overflow-hidden">
                  <div className="text-sm font-medium text-white truncate">Administrator</div>
                  <div className="text-xs text-gray-400">Super Admin</div>
               </div>
            </div>
         </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
         
         {/* Top Header - Clean White */}
         <header className="bg-white h-16 border-b border-gray-200 flex items-center justify-between px-4 sticky top-0 z-30 shadow-sm">
            <div className="flex items-center gap-4 flex-1">
               <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="lg:hidden p-2 text-gray-500 hover:bg-gray-100 rounded-md">
                  <Menu className="w-5 h-5" />
               </button>
               {/* CRM Search Bar */}
               <div className="flex items-center bg-gray-100 rounded-md px-3 py-1.5 w-full max-w-md focus-within:ring-1 focus-within:ring-blue-500 transition-all">
                  <Search className="w-4 h-4 text-gray-400 mr-2" />
                  <input 
                     type="text" 
                     placeholder="Search by name, email, or ID..." 
                     value={search}
                     onChange={(e) => setSearch(e.target.value)}
                     className="bg-transparent border-none text-sm focus:outline-none w-full placeholder-gray-500 text-gray-900"
                  />
               </div>
            </div>
            
            <div className="flex items-center gap-3">
               <button className="p-2 text-gray-500 hover:bg-gray-100 rounded-full relative">
                  <Bell className="w-5 h-5" />
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
               </button>
               <button onClick={() => navigate('/')} className="text-xs font-semibold text-gray-600 hover:text-gray-900 border border-gray-300 px-3 py-1.5 rounded-md bg-white hover:bg-gray-50 transition-colors">Exit</button>
            </div>
         </header>

         {/* Dashboard Content */}
         <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
            
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
               <h1 className="text-xl font-bold text-gray-900">User Management</h1>
               <button 
                  onClick={exportCSV} 
                  className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
               >
                  <Download className="w-4 h-4" />
                  Export Data
               </button>
            </div>

            {/* KPI Cards - Enterprise Style */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
               {[
                 { label: 'Total Users', value: users.length, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
                 { label: 'Active Pros', value: users.filter(u => u.subscription_status === 'pro').length, icon: Crown, color: 'text-amber-600', bg: 'bg-amber-50' },
                 { label: 'Daily Capacity', value: users.reduce((acc, curr) => acc + (curr.max_daily_credits || (curr.subscription_status === 'pro' ? 15 : 5)), 0), icon: LayoutDashboard, color: 'text-emerald-600', bg: 'bg-emerald-50' }
               ].map((stat, i) => (
                 <div key={i} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex items-start justify-between">
                    <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{stat.label}</p>
                        <div className="flex items-baseline gap-2">
                           <h3 className="text-2xl font-bold text-gray-900">{stat.value}</h3>
                           {i === 0 && <span className="text-xs font-medium text-green-600 flex items-center"><ArrowUpRight className="w-3 h-3 ml-0.5" /> 12%</span>}
                        </div>
                    </div>
                    <div className={`p-2 rounded-md ${stat.bg}`}>
                       <stat.icon className={`w-5 h-5 ${stat.color}`} />
                    </div>
                 </div>
               ))}
            </div>

            {/* Data Table */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
               {/* Controls */}
               <div className="px-5 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3 bg-gray-50/50 rounded-t-lg">
                  <div className="flex gap-2">
                     {['all', 'pro', 'normal'].map((status) => (
                        <button
                           key={status}
                           onClick={() => { setFilterStatus(status as any); setCurrentPage(1); }}
                           className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-all ${
                              filterStatus === status 
                                ? 'bg-white border-gray-300 text-gray-900 shadow-sm' 
                                : 'bg-transparent border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                           }`}
                        >
                           {status === 'all' ? 'All Users' : status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                     ))}
                  </div>
                  <div className="text-xs text-gray-500">
                     Showing <strong>{filteredUsers.length}</strong> records
                  </div>
               </div>

               {loading ? (
                  <div className="p-12 flex justify-center">
                     <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                  </div>
               ) : (
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                     <table className="w-full text-left border-collapse min-w-[700px]">
                        <thead>
                           <tr className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
                              <th className="px-5 py-3 font-semibold w-10">#</th>
                              <th className="px-5 py-3 font-semibold">User</th>
                              <th className="px-5 py-3 font-semibold">Status</th>
                              <th className="px-5 py-3 font-semibold">Usage</th>
                              <th className="px-5 py-3 font-semibold">Role</th>
                              <th className="px-5 py-3 font-semibold text-right">Actions</th>
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                           {paginatedUsers.map((user, idx) => (
                              <tr key={user.id} className="hover:bg-blue-50/30 transition-colors text-sm group">
                                 <td className="px-5 py-3 text-gray-400 text-xs">{(currentPage-1)*ITEMS_PER_PAGE + idx + 1}</td>
                                 <td className="px-5 py-3">
                                    <div className="flex items-center gap-3">
                                       <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center text-gray-600 font-bold text-xs border border-gray-200">
                                          {user.avatar_url ? (
                                             <img src={user.avatar_url} alt="" className="w-full h-full rounded object-cover" />
                                          ) : (
                                             (user.name || user.email || '?').charAt(0).toUpperCase()
                                          )}
                                       </div>
                                       <div>
                                          <div className="font-medium text-gray-900">{user.name || 'Unknown'}</div>
                                          <div className="text-xs text-gray-500">{user.email}</div>
                                       </div>
                                    </div>
                                 </td>
                                 <td className="px-5 py-3">
                                    {user.subscription_status === 'pro' ? (
                                       <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-bold bg-green-100 text-green-700 border border-green-200">
                                          PRO
                                       </span>
                                    ) : (
                                       <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                          FREE
                                       </span>
                                    )}
                                 </td>
                                  <td className="px-5 py-3">
                                    {/* Usage Bar - daily_ai_usage_count is USED COUNT */}
                                    {(() => {
                                       const limit = user.max_daily_credits || (user.subscription_status === 'pro' ? 15 : 5);
                                       const used = user.daily_ai_usage_count ?? 0;
                                       const percentage = Math.min((used / limit) * 100, 100);
                                       const isHighUsage = percentage > 80;
                                       
                                       return (
                                          <div className="w-32">
                                             <div className="flex justify-between items-center mb-1">
                                                <span className="text-xs text-gray-600 font-medium">{used}/{limit} used</span>
                                                <span className={`text-[10px] ${isHighUsage ? 'text-amber-600' : 'text-gray-400'}`}>
                                                   {percentage.toFixed(0)}%
                                                </span>
                                             </div>
                                             <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                                                <div 
                                                   className={`h-full rounded-full transition-all duration-500 ${isHighUsage ? 'bg-amber-500' : (user.subscription_status === 'pro' ? 'bg-green-500' : 'bg-blue-500')}`} 
                                                   style={{ width: `${percentage}%` }} 
                                                />
                                             </div>
                                          </div>
                                       );
                                    })()}
                                 </td>
                                 <td className="px-5 py-3">
                                    <span className="text-xs text-gray-500 bg-white border border-gray-200 px-1.5 py-0.5 rounded capitalize">
                                       {user.role}
                                    </span>
                                 </td>
                                 <td className="px-5 py-3 text-right">
                                    <div className="flex items-center justify-end gap-2 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                                       <button
                                          onClick={() => handleToggleStatus(user.id, user.subscription_status)}
                                          disabled={actionLoading === user.id || user.role === 'admin'}
                                          className={`
                                             text-xs font-medium px-3 py-1.5 rounded border transition-colors
                                             ${user.subscription_status === 'pro' 
                                                ? 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50' 
                                                : 'bg-white text-blue-600 border-blue-200 hover:border-blue-300 hover:bg-blue-50'}
                                             ${actionLoading === user.id ? 'opacity-50 cursor-wait' : ''}
                                          `}
                                       >
                                          {actionLoading === user.id ? (
                                             <Loader2 className="w-3 h-3 animate-spin" />
                                          ) : user.subscription_status === 'pro' ? (
                                             'Downgrade'
                                          ) : (
                                             'Upgrade'
                                          )}
                                       </button>
                                       <button className="text-gray-400 hover:text-gray-600">
                                          <MoreHorizontal className="w-4 h-4" />
                                       </button>
                                    </div>
                                 </td>
                              </tr>
                           ))}
                        </tbody>
                     </table>
                  </div>
               )}

               {/* Pagination */}
               <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between rounded-b-lg">
                  <div className="flex gap-1">
                     <button 
                         onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                         disabled={currentPage === 1}
                         className="p-1 px-2 text-xs font-medium border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                        Prev
                     </button>
                     <button 
                         onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                         disabled={currentPage === totalPages}
                         className="p-1 px-2 text-xs font-medium border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                        Next
                     </button>
                  </div>
                  <span className="text-xs text-gray-500">
                     Page {currentPage} of {totalPages || 1}
                  </span>
               </div>
            </div>

         </main>
      </div>
    </div>
  );
};

export default AdminPage;
